import * as React from 'react'
import {forwardRef, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {AnimatePresence} from 'framer-motion'

import {Box, BoxProps, Spacer, sx, themeGet} from '../..'
import {useRefObjectAsForwardedRef} from '../../hooks/useRefObjectAsForwardedRef'
import {RuleBuilderCondition} from './types'
import RuleStepLabel from './RuleStepLabel'
import RuleStepConnector from './RuleStepConnector'
import {useRuleBuilderStepsContext} from './context/RuleBuilderStepsContext'

interface InternalRuleBuilderStepProps extends BoxProps {
  /**
   * A unique id for the step
   */
  id?: string
  /**
   * Content to render in the step
   */
  children?: React.ReactNode
  /**
   * Whether the step is selected
   */
  isSelected?: boolean
  /**
   * The label that should be displayed in the Rule Builder Step label
   */
  label?: string | React.ReactNode
  /**
   * Handler for select events
   */
  onSelect?: () => void
  /**
   * The loading or error state of the Step
   */
  state?: 'loading' | 'error'
  /**
   * The condition of the step
   */
  condition: RuleBuilderCondition
  /**
   * Whether the step is completed
   */
  isCompletedStep?: boolean
  /**
   * Function that synchronizes with the underlying DOM element
   */
  stepRef?: (element: HTMLLIElement | null) => void
}
interface StepInternalConfig {
  index: number
  isLastStep?: boolean
  isSecondToLastStep?: boolean
}
interface FullRuleBuilderStepProps extends InternalRuleBuilderStepProps, StepInternalConfig {}
export type RuleBuilderStepProps = FullRuleBuilderStepProps

const StyledStep = styled(Box)<RuleBuilderStepProps>`
  position: relative;
  max-width: 100%;
  padding: 8px;
  /* margin: 4px; */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* flex: 1 0 auto; */
  justify-content: flex-start;
  &:hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  ${sx}
`
const StepContent = styled(Box)`
  width: 100%;
  margin-left: ${themeGet('space.2')};
`

const RuleBuilderStep = forwardRef(
  (
    {
      children,
      index,
      isSelected: isSelectedProp,
      label,
      onSelect,
      isLastStep,
      isSecondToLastStep,
      isCompletedStep,
      stepRef,
      condition = 'when',
      ...props
    }: RuleBuilderStepProps,
    forwardedRef
  ) => {
    const containerRef = useRef<HTMLLIElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    const {isError, isLoading, onSelectStep, activeStep} = useRuleBuilderStepsContext()
    const [isFocused, setIsFocused] = useState(false)

    const handleSelect = (i: number) => {
      if (onSelectStep) {
        onSelectStep(i)
      }
    }

    const handleFocus = () => {
      setIsFocused(true)
    }

    const handleBlur = () => {
      setIsFocused(false)
    }
    const isActive = isSelectedProp || isFocused || index === activeStep

    useEffect(() => {
      stepRef?.(containerRef.current)
    }, [containerRef, stepRef])

    let directionValue
    if (index === 0) directionValue = 'down'
    else if (index === 1) directionValue = 'right'
    else directionValue = 'down'

    return (
      <StyledStep
        // aria-selected={isSelectedProp}
        // aria-labelledby={`step-label-${label}`}
        onClick={() => handleSelect(index)}
        as="li"
        tabIndex={isActive ? 0 : -1}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isSelected={isActive}
        // @TODO: TS has bested me, we need to properly fwd ref
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={containerRef}
      >
        <Box style={{width: '100%', display: 'flex', alignItems: 'center'}}>
          {isLastStep ? (
            <Box as="div" sx={{mt: '-24px', ml: '8px', pb: 1, pl: 2}}>
              <AnimatePresence exitBeforeEnter>
                <RuleStepLabel condition={condition} isSelected={isActive} label={label} />
              </AnimatePresence>
            </Box>
          ) : (
            <Box as="div">
              <AnimatePresence exitBeforeEnter>
                <RuleStepLabel
                  condition={condition}
                  isSelected={isActive}
                  isCompletedStep={isCompletedStep}
                  label={label}
                />
              </AnimatePresence>
            </Box>
          )}
        </Box>
        <RuleStepConnector
          index={index}
          isSelectedStep={isActive}
          isCompletedStep={isCompletedStep}
          isLastStep={isLastStep}
          direction={directionValue}
          sx={{ml: 1, mt: 2}}
        >
          <StepContent>
            {children}
            <Spacer axis="vertical" size={isSecondToLastStep ? 32 : 20} />
          </StepContent>
        </RuleStepConnector>
      </StyledStep>
    )
  }
)

RuleBuilderStep.displayName = 'RuleBuilderStep'
export default RuleBuilderStep
